import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../store/authStore';
import Button from './Button';
import PostCard from './PostCard';
import UserName from './UserName';
import { X, Heart, Edit2, Trash2 } from 'lucide-react';
import supabase from "../lib/supabase";

const schema = z.object({
  content: z.string().min(1, 'Reply cannot be empty'),
});

type FormData = z.infer<typeof schema>;

interface PostDetailsModalProps {
  post: any;
  onClose: () => void;
  onUpdate: () => void;
}

export default function PostDetailsModal({ post, onClose, onUpdate }: PostDetailsModalProps) {
  const { user } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [localPost, setLocalPost] = React.useState(post);
  const [replies, setReplies] = React.useState<any[]>([]);
  const [isLoadingReplies, setIsLoadingReplies] = React.useState(false);
  const [editingReplyId, setEditingReplyId] = React.useState<string | null>(null);
  const [editContent, setEditContent] = React.useState('');

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema)
  });

  // Update local post when prop changes
  React.useEffect(() => {
    setLocalPost(post);
  }, [post]);

  // Fetch replies when modal opens
  React.useEffect(() => {
    const fetchReplies = async () => {
      try {
        setIsLoadingReplies(true);
        const { data, error } = await supabase
          .from('post_replies')
          .select(`
            *,
            author:author_id(
              id,
              full_name,
              display_name,
              verified,
              avatar_url
            )
          `)
          .eq('post_id', post.id)
          .order('created_at', { ascending: true });

        if (error) throw error;
        setReplies(data || []);
      } catch (error) {
        console.error('Error fetching replies:', error);
      } finally {
        setIsLoadingReplies(false);
      }
    };

    fetchReplies();
  }, [post.id]);

  const handlePostUpdate = async () => {
    try {
      // Fetch latest post data
      const { data: postData, error: postError } = await supabase
        .from('community_posts')
        .select(`
          *,
          author:author_id(
            id,
            full_name,
            display_name,
            verified,
            avatar_url
          ),
          category:category_id(
            id,
            name
          ),
          likes
        `)
        .eq('id', post.id)
        .single();

      if (postError) throw postError;

      // Fetch latest replies
      const { data: repliesData, error: repliesError } = await supabase
        .from('post_replies')
        .select(`
          *,
          author:author_id(
            id,
            full_name,
            display_name,
            verified,
            avatar_url
          )
        `)
        .eq('post_id', post.id)
        .order('created_at', { ascending: true });

      if (repliesError) throw repliesError;

      // Update local state
      if (postData) setLocalPost(postData);
      if (repliesData) setReplies(repliesData);

      // Notify parent
      onUpdate();
    } catch (error) {
      console.error('Error updating post data:', error);
    }
  };

  const handleReplyLike = async (replyId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase.rpc('manage_reply_likes', {
        reply_id: replyId,
        user_id: user.id
      });

      if (error) throw error;

      // Update local state
      const updatedReplies = replies.map(reply => {
        if (reply.id === replyId) {
          const hasLiked = reply.likes?.includes(user.id);
          return {
            ...reply,
            likes: hasLiked 
              ? reply.likes.filter((id: string) => id !== user.id)
              : [...(reply.likes || []), user.id],
            like_count: hasLiked ? Math.max(0, reply.like_count - 1) : (reply.like_count || 0) + 1
          };
        }
        return reply;
      });

      setReplies(updatedReplies);
    } catch (error) {
      console.error('Error liking reply:', error);
    }
  };

  const handleReplyEdit = async (replyId: string) => {
    try {
      const { error } = await supabase.rpc('manage_reply_update', {
        reply_id: replyId,
        new_content: editContent,
        user_id: user?.id
      });

      if (error) throw error;

      // Update local state
      const updatedReplies = replies.map(reply => 
        reply.id === replyId ? { ...reply, content: editContent } : reply
      );
      setReplies(updatedReplies);
      setEditingReplyId(null);
      setEditContent('');
    } catch (error) {
      console.error('Error updating reply:', error);
    }
  };

  const handleReplyDelete = async (replyId: string) => {
    if (!window.confirm('Are you sure you want to delete this reply?')) return;

    try {
      const { error } = await supabase
        .from('post_replies')
        .delete()
        .eq('id', replyId);

      if (error) throw error;

      // Update local state
      setReplies(replies.filter(reply => reply.id !== replyId));
      await handlePostUpdate(); // Update post to get new reply count
    } catch (error) {
      console.error('Error deleting reply:', error);
    }
  };

  const onSubmit = async (data: FormData) => {
    if (!user) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const { error: replyError } = await supabase
        .from('post_replies')
        .insert({
          post_id: post.id,
          author_id: user.id,
          content: data.content,
        });

      if (replyError) throw replyError;

      // Refresh post data and replies
      await handlePostUpdate();
      reset();
    } catch (error: any) {
      console.error('Error submitting reply:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle post deletion
  const handlePostDelete = () => {
    onClose(); // Close the modal first
    onUpdate(); // Then update the parent component
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-surface rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-surface border-b border-neutral p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold font-montserrat text-primary">Discussion</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-light rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Original Post */}
          <PostCard 
            post={localPost} 
            showReplies={false}
            onUpdate={handlePostUpdate}
            onDelete={handlePostDelete}
          />

          {/* Replies Section */}
          <div className="space-y-4">
            <h3 className="font-montserrat font-semibold text-primary">
              Replies ({replies.length})
            </h3>

            {isLoadingReplies ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent mx-auto"></div>
                <p className="mt-2 text-sm text-neutral">Loading replies...</p>
              </div>
            ) : replies.length > 0 ? (
              <div className="space-y-4">
                {replies.map((reply) => (
                  <div key={reply.id} className="bg-surface-light rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <img
                          src={reply.author.avatar_url || `https://ui-avatars.com/api/?name=${reply.author.display_name || reply.author.full_name}`}
                          alt={reply.author.display_name || reply.author.full_name}
                          className="w-8 h-8 rounded-full"
                        />
                        <div>
                          <UserName
                            name={reply.author.display_name || reply.author.full_name}
                            verified={reply.author.verified}
                            size="sm"
                            userId={reply.author.id}
                          />
                          <p className="text-xs text-neutral">
                            {new Date(reply.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      {(user?.id === reply.author_id || user?.id === localPost.community?.leader_id) && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setEditingReplyId(reply.id);
                              setEditContent(reply.content);
                            }}
                            className="p-2 text-neutral hover:text-primary transition-colors"
                            title="Edit reply"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleReplyDelete(reply.id)}
                            className="p-2 text-neutral hover:text-secondary-dark transition-colors"
                            title="Delete reply"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {editingReplyId === reply.id ? (
                      <div className="mb-3">
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-neutral bg-surface-light min-h-[100px]"
                        />
                        <div className="flex justify-end space-x-2 mt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingReplyId(null);
                              setEditContent('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleReplyEdit(reply.id)}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-neutral text-sm mb-2">{reply.content}</p>
                        <button
                          onClick={() => handleReplyLike(reply.id)}
                          disabled={!user}
                          className={`flex items-center text-sm ${
                            reply.likes?.includes(user?.id)
                              ? 'text-accent hover:text-accent-dark' 
                              : 'text-neutral hover:text-primary'
                          } transition-colors`}
                        >
                          <Heart className={`w-4 h-4 mr-1 ${reply.likes?.includes(user?.id) ? 'fill-current' : ''}`} />
                          {reply.like_count || 0}
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-neutral py-4">No replies yet</p>
            )}
          </div>

          {/* Reply Form */}
          {user && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {error && (
                <div className="bg-secondary-dark bg-opacity-10 text-secondary-dark p-4 rounded-xl">
                  {error}
                </div>
              )}

              <div>
                <textarea
                  {...register('content')}
                  className="w-full h-24 px-4 py-3 rounded-xl border border-neutral bg-surface-light text-primary placeholder:text-neutral focus:border-accent focus:ring-1 focus:ring-accent resize-none"
                  placeholder="Write your reply..."
                />
                {errors.content && (
                  <p className="mt-1 text-sm text-secondary-dark">{errors.content.message}</p>
                )}
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Reply'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}